import 'styles/loader.scss';

import React from 'react';
import {
    Route,
    Switch,
    Redirect,
    BrowserRouter,
} from 'react-router-dom';
import Async from 'react-async';
import { Provider } from 'mobx-react';
import { registerLocale } from 'react-datepicker';
import localeRu from 'date-fns/locale/ru';
import initializeStore from 'stores';
import SalesPage from 'pages/SalesPage';
import AdditionalPointsPage from 'pages/AdditionalPointsPage';
import RatingPage from 'pages/RatingPage';
import ShopPage from 'pages/ShopPage';
import CartPage from 'pages/CartPage';
import OrdersPage from 'pages/OrdersPage';
import NewsListPage from 'pages/NewsListPage';
import NewsPage from 'pages/NewsPage';
import SupervisorSalesPage from 'pages/SupervisorSalesPage';
import SupervisorOrdersPage from 'pages/SupervisorOrdersPage';
import SupervisorRatingPage from 'pages/SupervisorRatingPage';
import ProfilePage from 'pages/ProfilePage';
import Page404 from 'pages/Page404';
import FadeTransition from 'components/FadeTransition';
import ScrollToTop from 'components/ScrollToTop';
import { isProdEnvironment } from './helpers/helpers';
import ExternalRedirect from './ExternalRedirect';
import AuthLoginPage from './pages/AuthLoginPage';
import NotificationBarLayout from './layouts/NotificationBarLayout';

registerLocale('ru', localeRu);

const store = initializeStore();

const getAuthUser = () => store.profile.getClientInfo();

const hasAuthUser = () => Object.keys(store.profile.user).length !== 0;

const AppRoute = ({ component: Component, layout: Layout, meta = {
    phoneRequired: undefined,
}, verify, ...rest }) => {
    if (!Component) {
        return <Redirect to={{ pathname: '/login' }} />;
    }

    const { isSeller, isSupervisor } = store.profile.user;
    const showSeller = !!meta.showSeller;
    const showSupervisor = !!meta.showSupervisor;
    const isPublic = !!meta.public;
    const loggedIn = hasAuthUser();
    const notLoginPage = window.location.pathname.indexOf('/login') === -1;

    return (
        <Route {...rest} render={(props) => (
            <React.Fragment>
                {(!isPublic
                    && ((!loggedIn && notLoginPage)
                        || (!isSeller && showSeller)
                        || (!isSupervisor && showSupervisor)))
                    && (
                        <Redirect to={{ pathname: '/login' }} />
                    )}
                <NotificationBarLayout>
                    {Layout
                        ? (
                            <Layout>
                                <Component {...props} />
                            </Layout>
                        )
                        : (
                            <Component {...props} />
                        )}
                </NotificationBarLayout>
            </React.Fragment>
        )} />
    );
};
const externalLoginUrl = isProdEnvironment
    ? 'https://b2b-scb.sovcomins.ru/Login' : 'https://wvt-sk-bb2-app1.sovcombank.group:50001';

const Router = () => (
    <BrowserRouter>
        <ScrollToTop callback={() => {
            document.getElementsByTagName('body')[0].scrollTop = 0;
            getAuthUser();
        }}>
            <Switch>
                <ExternalRedirect from='/login' to={externalLoginUrl} />
                <AppRoute exact path="/" />
                <AppRoute
                    exact
                    path="/auth/login"
                    component={AuthLoginPage}
                    meta={{
                        public: true,
                    }}
                />
                <AppRoute
                    exact
                    path="/sales"
                    component={SalesPage}
                    meta={{
                        showSeller: true,
                    }}
                />
                <AppRoute
                    exact
                    path="/sales/additional"
                    component={AdditionalPointsPage}
                    meta={{
                        showSeller: true,
                    }}
                />
                <AppRoute
                    exact
                    path="/rating"
                    component={RatingPage}
                    meta={{
                        showSeller: true,
                    }}
                />
                <AppRoute
                    exact
                    path="/shop"
                    component={ShopPage}
                    meta={{
                        showSeller: true,
                    }}
                />
                <AppRoute
                    exact
                    path="/cart"
                    component={CartPage}
                    meta={{
                        showSeller: true,
                    }}
                />
                <AppRoute
                    exact
                    path="/orders"
                    component={OrdersPage}
                    meta={{
                        showSeller: true,
                    }}
                />
                <AppRoute
                    exact
                    path="/news"
                    component={NewsListPage}
                />
                <AppRoute
                    exact
                    path="/news/:id"
                    component={NewsPage}
                />
                <AppRoute
                    exact
                    path="/supervisor/sales"
                    component={SupervisorSalesPage}
                    meta={{
                        showSupervisor: true,
                    }}
                />
                <AppRoute
                    exact
                    path="/supervisor/orders"
                    component={SupervisorOrdersPage}
                    meta={{
                        showSupervisor: true,
                    }}
                />
                <AppRoute
                    exact
                    path="/supervisor/rating"
                    component={SupervisorRatingPage}
                    meta={{
                        showSupervisor: true,
                    }}
                />
                <AppRoute
                    exact
                    path="/profile"
                    component={ProfilePage}
                    meta={{
                        showSeller: true,
                    }}
                />
                <AppRoute
                    exact
                    path="*"
                    component={Page404}
                />
            </Switch>
        </ScrollToTop>
    </BrowserRouter>
);

const App = () => (
    <React.Fragment>
        <Provider store={store}>
            <Async promiseFn={getAuthUser}>
                {({ isLoading }) => (
                    <FadeTransition loading={isLoading}>
                        {!isLoading && <Router />}
                    </FadeTransition>
                )}
            </Async>
        </Provider>
    </React.Fragment>
);

export default App;
